<template>
  <div class="el-main">
    <DesignRirekishoJobCategoryCard :current-card-id="currentCardId" />
    <RirekishoEduHistoryCard
      :basic-data="basicData"
      :format-id="formatId"
      :current-card-id="currentCardId"
      :preview-errors="previewErrors"
    />
    <DesignRirekishoSkillsCard
      :basic-data="basicData"
      :current-card-id="currentCardId"
    />
    <RirekishoLicenseCard
      :basic-data="basicData"
      :format-id="formatId"
      :current-card-id="currentCardId"
      :preview-errors="previewErrors"
      :example-sentences="exampleSentences"
    />
    <DesignRirekishoJobDetailHistoryCard
      :basic-data="basicData"
      :current-card-id="currentCardId"
      :preview-errors="previewErrors"
    />
    <DesignRirekishoMyprCard
      :current-card-id="currentCardId"
      :example-sentences="exampleSentences"
      :textarea-min-rows="textareaMinRows"
      :preview-errors="previewErrors"
    />
    <DesignRirekishoHobbyPickerCard :current-card-id="currentCardId" />
    <RirekishoCreateDateCard
      :current-card-id="currentCardId"
      :show-quick-email-banner="false"
    />

    <div class="card" v-show="currentCardId == 'confirm'">
      <div class="overflow-scroll-y">
        <div class="card-header">
          <h2>作成した履歴書を確認</h2>
          <p>
            修正する場合は、プレビューの修正欄をクリックまたはステップを戻ることで修正することができます
          </p>
        </div>
      </div>
    </div>

    <div
      class="card"
      v-show="currentCardId == 'output'"
      v-if="getMode != 'production1'"
    >
      <DownloadItem
        :formatId="formatId"
        :isSendYagiPostEnabled="isSendYagiPostEnabled"
        :isSendYagiEmailEnabled="isSendYagiEmailEnabled"
        :mailPdfEnabled="mailPdfEnabled"
        :downloadPdfEnabled="downloadPdfEnabled"
        :downloadActiveCollapses="downloadActiveCollapses"
        @update:isSendYagiEmailEnabled="isSendYagiEmailEnabled = $event"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, onBeforeUnmount, ref, watch } from 'vue'
import { emitter } from '@/plugins/emitter'
import { useHostServices } from '@/composables/format-edit/useHostServices'
import { useFormatLocalData } from '@/composables/format-edit/useFormatLocalData'
import {
  createEmptyRirekishoFormatData,
  provideRirekishoFormatData,
  type RirekishoExampleSentences
} from '@/composables/format-edit/useRirekishoFormatData'
import {
  provideRirekishoHistories,
  useRirekishoHistories
} from '@/composables/format-edit/useRirekishoHistories'
import RirekishoEduHistoryCard from '@/components/format-edit/rirekisho/RirekishoEduHistoryCard.vue'
import RirekishoLicenseCard from '@/components/format-edit/rirekisho/RirekishoLicenseCard.vue'
import RirekishoCreateDateCard from '@/components/format-edit/rirekisho/RirekishoCreateDateCard.vue'
import DesignRirekishoJobCategoryCard from '@/components/format-edit/design-rirekisho/DesignRirekishoJobCategoryCard.vue'
import DesignRirekishoSkillsCard from '@/components/format-edit/design-rirekisho/DesignRirekishoSkillsCard.vue'
import DesignRirekishoJobDetailHistoryCard from '@/components/format-edit/design-rirekisho/DesignRirekishoJobDetailHistoryCard.vue'
import DesignRirekishoMyprCard from '@/components/format-edit/design-rirekisho/DesignRirekishoMyprCard.vue'
import DesignRirekishoHobbyPickerCard from '@/components/format-edit/design-rirekisho/DesignRirekishoHobbyPickerCard.vue'
import DownloadItem from '@/components/formats/CommonParts/DownloadItem.vue'
import type { BasicData } from '@/utils/format-edit/basicData'

const props = defineProps<{
  basicData: BasicData
  formatId?: string
  previewFormat?: {
    errorMessages?: Record<string, string>
    optionItems?: Record<string, unknown>
  } | null
  currentCardId?: string
  exampleSentences?: RirekishoExampleSentences
}>()

defineEmits<{
  (e: 'update-basic-data', payload: Partial<BasicData>): void
}>()

const { formLib } = useHostServices()

const { formatData } = useFormatLocalData({
  saveName: 'rirekisho',
  saveOtherName: 'shokumukeirekisho',
  createEmpty: createEmptyRirekishoFormatData
})
provideRirekishoFormatData(formatData)

const histories = useRirekishoHistories(formatData)
provideRirekishoHistories(histories)

const mailPdfEnabled = ref(true)
const downloadPdfEnabled = ref(true)
const isSendYagiPostEnabled = ref(true)
const isSendYagiEmailEnabled = ref(true)
const downloadActiveCollapses = ref(['postcard'])

const previewErrors = computed(() => {
  if (!props.previewFormat || !props.previewFormat.errorMessages) return {}
  return props.previewFormat.errorMessages
})

const getMode = computed(() => process.env.VUE_APP_MODE)
const textareaMinRows = computed(() => 3)

function dataChanged() {
  mailPdfEnabled.value = true
  downloadPdfEnabled.value = true
}

watch(
  () => formatData.createDate,
  (newValue) => {
    if (newValue == null) return
    formatData.writeDate = formLib.dateFormat(
      newValue as Date,
      'yyyy年 m月 d日'
    )
  }
)

onBeforeMount(() => {
  if (formatData.createDate === null) {
    formatData.createDate = formLib.getToday()
  }
  if (!formatData.skills) formatData.skills = []
  if (!formatData.hobbies) formatData.hobbies = []
  if (!formatData.jobDetailHistories) formatData.jobDetailHistories = []
  emitter.on('basic-data-changed', dataChanged)
  emitter.on('format-data-changed', dataChanged)
})

onBeforeUnmount(() => {
  emitter.off('basic-data-changed', dataChanged)
  emitter.off('format-data-changed', dataChanged)
})

defineExpose({
  saveName: 'rirekisho',
  formatData,
  addEducationalHistory: histories.addEducationalHistory
})
</script>

<style lang="scss" scoped>
* {
  list-style: none;
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

img {
  width: 100%;
  vertical-align: bottom;
}

.card-header h1,
.card-header h2 {
  padding-bottom: 1.5rem;
}
</style>
